import { Link } from "react-router-dom";
import Seo from "@/lib/seo/Seo";
import { getSeoForPath } from "@/lib/seo/routes.mjs";
import {
  Check,
  ArrowRight,
  ChevronRight,
  CreditCard,
  Monitor,
  Terminal as TerminalIcon,
  Wallet,
  Gift,
  Globe,
  MapPin,
  Users,
  Wrench,
  Compass,
  ShieldCheck,
} from "lucide-react";
import Reveal from "@/components/Reveal";
import ParticleField from "@/components/ParticleField";

/* ────────────────────────────────────────────────────────────
   Local-business payment environment illustration
   Same polished flat-fintech language used site-wide:
   navy palette, floating cards, soft drop shadows, dotted
   accents, gentle radial halos. Renders transparent so it
   floats on the navy hero.
   ──────────────────────────────────────────────────────────── */
function LocalPaymentsIllustration({ className = "" }) {
  const id = "syr";
  return (
    <svg
      viewBox="0 0 560 460"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      role="img"
      aria-label="Countertop POS terminal accepting a tap-to-pay card, with an itemized receipt and a Central New York service-area badge"
    >
      <defs>
        <linearGradient id={`${id}-navy`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#0A2552" />
          <stop offset="100%" stopColor="#061A3A" />
        </linearGradient>
        <linearGradient id={`${id}-accent`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#0072FF" />
          <stop offset="100%" stopColor="#3A8EFF" />
        </linearGradient>
        <linearGradient id={`${id}-card`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#FFFFFF" />
          <stop offset="100%" stopColor="#F6FAFF" />
        </linearGradient>
        <radialGradient id={`${id}-halo`} cx="50%" cy="50%" r="55%">
          <stop offset="0%" stopColor="#0072FF" stopOpacity="0.28" />
          <stop offset="70%" stopColor="#0072FF" stopOpacity="0.06" />
          <stop offset="100%" stopColor="#0072FF" stopOpacity="0" />
        </radialGradient>
        <pattern id={`${id}-dots`} x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
          <circle cx="1.4" cy="1.4" r="1.4" fill="#3A8EFF" fillOpacity="0.22" />
        </pattern>
        <filter id={`${id}-shadow`} x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur in="SourceAlpha" stdDeviation="8" />
          <feOffset dx="0" dy="14" result="off" />
          <feComponentTransfer><feFuncA type="linear" slope="0.35" /></feComponentTransfer>
          <feMerge><feMergeNode /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
        <filter id={`${id}-soft`} x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur in="SourceAlpha" stdDeviation="4" />
          <feOffset dx="0" dy="6" result="off" />
          <feComponentTransfer><feFuncA type="linear" slope="0.25" /></feComponentTransfer>
          <feMerge><feMergeNode /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>

      {/* halo behind everything */}
      <ellipse cx="290" cy="230" rx="240" ry="180" fill={`url(#${id}-halo)`} />

      {/* decorative accents */}
      <rect x="60" y="60" width="42" height="42" rx="12" fill="#0072FF" fillOpacity="0.14" transform="rotate(14 81 81)" />
      <rect x="470" y="82" width="34" height="34" rx="10" fill="#3A8EFF" fillOpacity="0.16" transform="rotate(-10 487 99)" />
      <circle cx="80" cy="360" r="34" fill="#0072FF" fillOpacity="0.12" />
      <circle cx="500" cy="360" r="26" fill="#3A8EFF" fillOpacity="0.16" />
      <rect x="230" y="26" width="120" height="120" rx="18" fill={`url(#${id}-dots)`} opacity="0.55" />

      {/* HERO — countertop POS terminal */}
      <g transform="translate(150 130)" filter={`url(#${id}-shadow)`}>
        {/* terminal body */}
        <rect x="0" y="60" width="260" height="180" rx="20" fill={`url(#${id}-navy)`} />
        {/* screen */}
        <rect x="14" y="74" width="232" height="120" rx="10" fill={`url(#${id}-card)`} />
        {/* status bar */}
        <rect x="14" y="74" width="232" height="16" rx="10" fill="#EAF3FF" />
        <circle cx="26" cy="82" r="3" fill="#0072FF" />
        <text x="36" y="86" fontFamily="Inter, system-ui, sans-serif" fontSize="8" fontWeight="800" fill="#061A3A">SWIPETRONICS · READY</text>
        <text x="232" y="86" textAnchor="end" fontFamily="Inter, system-ui, sans-serif" fontSize="8" fontWeight="700" fill="#061A3A" fillOpacity="0.55">14:32</text>
        {/* screen content — big amount */}
        <text x="30" y="120" fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="700" fill="#061A3A" fillOpacity="0.55">Amount due</text>
        <text x="30" y="152" fontFamily="Inter, system-ui, sans-serif" fontSize="32" fontWeight="900" fill="#061A3A">$42.75</text>
        {/* pay chip */}
        <rect x="30" y="164" width="200" height="22" rx="11" fill={`url(#${id}-accent)`} />
        <text x="130" y="179" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="800" fill="#FFFFFF">TAP · INSERT · SWIPE</text>
        {/* keypad rows */}
        <g transform="translate(20 202)">
          {[0,1,2,3,4,5,6,7,8,9,10,11].map((i) => {
            const col = i % 4;
            const row = Math.floor(i / 4);
            return (
              <rect key={i} x={col * 56} y={row * 12} width="48" height="8" rx="3" fill="#3A8EFF" fillOpacity={i === 5 ? 1 : 0.32} />
            );
          })}
        </g>
        {/* card slot */}
        <rect x="90" y="234" width="80" height="4" rx="2" fill="#3A8EFF" fillOpacity="0.4" />
      </g>

      {/* Tap-to-pay CARD floating over the terminal */}
      <g transform="translate(310 84) rotate(-14 90 60)" filter={`url(#${id}-shadow)`}>
        <rect width="180" height="112" rx="14" fill={`url(#${id}-accent)`} />
        <rect y="0" width="180" height="34" rx="14" fill={`url(#${id}-navy)`} />
        <rect y="20" width="180" height="14" fill="#061A3A" />
        <text x="14" y="22" fontFamily="Inter, system-ui, sans-serif" fontSize="9" fontWeight="800" fill="#FFFFFF">SWIPETRONICS</text>
        {/* chip */}
        <rect x="14" y="46" width="30" height="22" rx="4" fill="#F6FAFF" />
        <line x1="14" y1="52" x2="44" y2="52" stroke="#061A3A" strokeOpacity="0.4" />
        <line x1="14" y1="60" x2="44" y2="60" stroke="#061A3A" strokeOpacity="0.4" />
        <line x1="20" y1="46" x2="20" y2="68" stroke="#061A3A" strokeOpacity="0.4" />
        <line x1="38" y1="46" x2="38" y2="68" stroke="#061A3A" strokeOpacity="0.4" />
        {/* contactless waves */}
        <g transform="translate(148 44)" fill="none" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round">
          <path d="M0 12 Q4 6 8 12" />
          <path d="M-4 16 Q4 2 12 16" />
        </g>
        {/* number groups */}
        <g transform="translate(14 84)" fontFamily="ui-monospace, Menlo, monospace">
          {[0,1,2,3].map((i) => (
            <rect key={i} x={i * 40} y="0" width="30" height="4" rx="2" fill="#FFFFFF" fillOpacity={i < 3 ? 0.5 : 0.95} />
          ))}
        </g>
        <text x="14" y="106" fontFamily="Inter, system-ui, sans-serif" fontSize="7" fontWeight="700" fill="#FFFFFF" fillOpacity="0.7">TAP TO PAY</text>
      </g>

      {/* Receipt card — bottom-left */}
      <g transform="translate(70 262)" filter={`url(#${id}-shadow)`}>
        <rect width="150" height="140" rx="12" fill="#FFFFFF" />
        <rect x="0" y="0" width="150" height="6" rx="3" fill={`url(#${id}-accent)`} />
        {/* logo strip */}
        <text x="14" y="24" fontFamily="Inter, system-ui, sans-serif" fontSize="8" fontWeight="900" fill="#061A3A">SWIPETRONICS</text>
        <text x="14" y="36" fontFamily="Inter, system-ui, sans-serif" fontSize="7" fontWeight="700" fill="#061A3A" fillOpacity="0.55">Local receipt · Central NY</text>
        {/* line items */}
        <g transform="translate(14 46)" fontFamily="ui-monospace, Menlo, monospace">
          {[
            { t: "Coffee", p: "$4.75" },
            { t: "Sandwich", p: "$9.50" },
            { t: "Muffin", p: "$3.50" },
          ].map((it, i) => (
            <g key={i} transform={`translate(0 ${i * 14})`}>
              <text fontSize="8" fontWeight="700" fill="#061A3A">{it.t}</text>
              <text x="122" textAnchor="end" fontSize="8" fontWeight="700" fill="#061A3A">{it.p}</text>
            </g>
          ))}
        </g>
        <line x1="14" y1="94" x2="136" y2="94" stroke="#061A3A" strokeOpacity="0.15" strokeDasharray="2 2" />
        <text x="14" y="108" fontFamily="Inter, system-ui, sans-serif" fontSize="8" fontWeight="700" fill="#061A3A" fillOpacity="0.55">Total</text>
        <text x="136" y="108" textAnchor="end" fontFamily="Inter, system-ui, sans-serif" fontSize="12" fontWeight="900" fill="#061A3A">$17.75</text>
        {/* approved badge */}
        <rect x="14" y="118" width="122" height="16" rx="8" fill={`url(#${id}-accent)`} />
        <text x="75" y="129" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="8" fontWeight="800" fill="#FFFFFF">APPROVED · #24817</text>
      </g>

      {/* Local service-area pill — bottom-right */}
      <g transform="translate(340 320)" filter={`url(#${id}-soft)`}>
        <rect width="188" height="56" rx="28" fill="#FFFFFF" />
        <g transform="translate(16 12)">
          <circle cx="16" cy="16" r="16" fill={`url(#${id}-accent)`} />
          <path d="M16 6 C11 6 8 9 8 14 c0 6 8 12 8 12 s8 -6 8 -12 c0 -5 -3 -8 -8 -8 z M16 14 a2 2 0 1 0 0.001 0 z" fill="#FFFFFF" />
        </g>
        <text x="60" y="26" fontFamily="Inter, system-ui, sans-serif" fontSize="9" fontWeight="700" fill="#061A3A" fillOpacity="0.55">Service area</text>
        <text x="60" y="42" fontFamily="Inter, system-ui, sans-serif" fontSize="12" fontWeight="900" fill="#061A3A">Central New York</text>
      </g>

      {/* pulse dot */}
      <circle cx="290" cy="184" r="3.5" fill="#0072FF">
        <animate attributeName="fill-opacity" values="0.2;1;0.2" dur="2.6s" repeatCount="indefinite" />
      </circle>
    </svg>
  );
}

/* ────────────────────────────────────────────────────────────
   Section-2 illustration: business owner reviewing a merchant
   statement (magnifying glass over line items, cost highlight)
   ──────────────────────────────────────────────────────────── */
function StatementReviewIllustration({ className = "" }) {
  const id = "syr2";
  return (
    <svg viewBox="0 0 520 440" xmlns="http://www.w3.org/2000/svg" className={className} role="img" aria-label="Business owner reviewing a merchant processing statement">
      <defs>
        <linearGradient id={`${id}-panel`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#EAF3FF" /><stop offset="100%" stopColor="#F4F8FF" />
        </linearGradient>
        <linearGradient id={`${id}-accent`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#0072FF" /><stop offset="100%" stopColor="#3A8EFF" />
        </linearGradient>
        <linearGradient id={`${id}-navy`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#0A2552" /><stop offset="100%" stopColor="#061A3A" />
        </linearGradient>
        <radialGradient id={`${id}-halo`} cx="50%" cy="50%" r="55%">
          <stop offset="0%" stopColor="#0072FF" stopOpacity="0.22" />
          <stop offset="70%" stopColor="#0072FF" stopOpacity="0.05" />
          <stop offset="100%" stopColor="#0072FF" stopOpacity="0" />
        </radialGradient>
        <pattern id={`${id}-dots`} x="0" y="0" width="18" height="18" patternUnits="userSpaceOnUse">
          <circle cx="1.4" cy="1.4" r="1.4" fill="#0072FF" fillOpacity="0.18" />
        </pattern>
        <filter id={`${id}-shadow`} x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur in="SourceAlpha" stdDeviation="8" /><feOffset dy="14" />
          <feComponentTransfer><feFuncA type="linear" slope="0.24" /></feComponentTransfer>
          <feMerge><feMergeNode /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>

      {/* soft panel */}
      <rect x="16" y="16" width="488" height="408" rx="28" fill={`url(#${id}-panel)`} />
      <rect x="16" y="16" width="488" height="408" rx="28" fill={`url(#${id}-dots)`} opacity="0.6" />
      <ellipse cx="260" cy="220" rx="220" ry="160" fill={`url(#${id}-halo)`} />

      {/* decorative shapes */}
      <rect x="52" y="52" width="42" height="42" rx="12" fill="#0072FF" fillOpacity="0.12" transform="rotate(-14 73 73)" />
      <rect x="432" y="52" width="34" height="34" rx="10" fill="#3A8EFF" fillOpacity="0.14" transform="rotate(12 449 69)" />
      <circle cx="70" cy="360" r="26" fill="#0072FF" fillOpacity="0.12" />
      <circle cx="450" cy="370" r="20" fill="#3A8EFF" fillOpacity="0.14" />

      {/* HERO — merchant statement */}
      <g transform="translate(96 74)" filter={`url(#${id}-shadow)`}>
        <rect width="270" height="298" rx="14" fill="#FFFFFF" stroke="#061A3A" strokeOpacity="0.08" />
        <rect x="0" y="0" width="270" height="6" rx="3" fill={`url(#${id}-accent)`} />
        <g transform="translate(20 24)" fontFamily="Inter, system-ui, sans-serif">
          <text fontSize="9" fontWeight="700" fill="#061A3A" fillOpacity="0.55">MERCHANT STATEMENT</text>
          <text y="22" fontSize="16" fontWeight="900" fill="#061A3A">Monthly Processing</text>
          <text y="40" fontSize="9" fontWeight="700" fill="#061A3A" fillOpacity="0.5">Volume · $84,320.15</text>
        </g>
        {/* row headers */}
        <g transform="translate(20 82)" fontFamily="Inter, system-ui, sans-serif">
          <rect width="230" height="18" rx="4" fill="#EAF3FF" />
          <text x="8" y="12" fontSize="8" fontWeight="800" fill="#061A3A" fillOpacity="0.6">FEE</text>
          <text x="230" y="12" textAnchor="end" fontSize="8" fontWeight="800" fill="#061A3A" fillOpacity="0.6">AMOUNT</text>
        </g>
        {/* line items */}
        <g transform="translate(20 108)" fontFamily="ui-monospace, Menlo, monospace">
          {[
            { l: "Interchange",       v: "$1,684.02",  hi: false },
            { l: "Assessment fees",   v: "$92.75",     hi: false },
            { l: "PCI compliance",    v: "$29.95",     hi: true },
            { l: "Monthly minimum",   v: "$25.00",     hi: true },
            { l: "Batch fees",        v: "$14.00",     hi: false },
            { l: "Statement fee",     v: "$12.00",     hi: true },
            { l: "Non-qualified",     v: "$248.60",    hi: true },
          ].map((it, i) => (
            <g key={i} transform={`translate(0 ${i * 22})`}>
              {it.hi && <rect x="-4" y="-2" width="238" height="20" rx="4" fill="#FFF4E4" />}
              {it.hi && <rect x="-4" y="-2" width="3" height="20" rx="1.5" fill="#F59E0B" />}
              <text fontSize="9" fontWeight="700" fill="#061A3A">{it.l}</text>
              <text x="230" textAnchor="end" fontSize="9" fontWeight="800" fill="#061A3A">{it.v}</text>
            </g>
          ))}
        </g>
      </g>

      {/* Magnifying glass — highlighting a fee */}
      <g transform="translate(272 216) rotate(18 62 62)" filter={`url(#${id}-shadow)`}>
        <circle cx="62" cy="62" r="52" fill="rgba(255,255,255,0.9)" stroke={`url(#${id}-accent)`} strokeWidth="7" />
        <circle cx="62" cy="62" r="52" fill="url(#'+id+'-accent)" fillOpacity="0.05" />
        <line x1="100" y1="100" x2="140" y2="140" stroke="#061A3A" strokeWidth="10" strokeLinecap="round" />
        <line x1="100" y1="100" x2="140" y2="140" stroke={`url(#${id}-accent)`} strokeWidth="5" strokeLinecap="round" />
        {/* small "$29.95" inside lens */}
        <text x="62" y="58" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="800" fill="#061A3A" opacity="0.55">FLAGGED</text>
        <text x="62" y="76" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="16" fontWeight="900" fill="#0072FF">$29.95</text>
      </g>

      {/* "You could be saving" callout — top-right */}
      <g transform="translate(340 60)" filter={`url(#${id}-shadow)`}>
        <rect width="146" height="72" rx="14" fill={`url(#${id}-navy)`} />
        <rect x="0" y="0" width="146" height="5" rx="2.5" fill={`url(#${id}-accent)`} />
        <text x="14" y="26" fontFamily="Inter, system-ui, sans-serif" fontSize="9" fontWeight="700" fill="#FFFFFF" fillOpacity="0.6">Potential monthly</text>
        <text x="14" y="50" fontFamily="Inter, system-ui, sans-serif" fontSize="20" fontWeight="900" fill="#FFFFFF">$412</text>
        <text x="14" y="64" fontFamily="Inter, system-ui, sans-serif" fontSize="8.5" fontWeight="700" fill="#3A8EFF">avoidable fees</text>
      </g>

      {/* pulse */}
      <circle cx="132" cy="374" r="3" fill="#0072FF">
        <animate attributeName="fill-opacity" values="0.2;1;0.2" dur="2.6s" repeatCount="indefinite" />
      </circle>
    </svg>
  );
}

/* ────────────────────────────────────────────────────────────
   Section-4 illustration: 4 merchant types connected to a
   central card reader (restaurant / retail / salon / service)
   ──────────────────────────────────────────────────────────── */
function MerchantMixIllustration({ className = "" }) {
  const id = "syr4";
  const nodes = [
    { x: 90,  y: 100, label: "Restaurant",  path: "M6 2h20l-2 6H8Z M8 8h16v18H8Z" },
    { x: 410, y: 100, label: "Retail",      path: "M4 6h24l-2 4H6Z M6 10h22v18H6Z M12 10v18 M22 10v18" },
    { x: 90,  y: 300, label: "Convenience", path: "M4 4h24v6H4Z M4 10h24v18H4Z M12 4v6 M20 4v6" },
    { x: 410, y: 300, label: "Salons",      path: "M6 6 C6 10 12 16 16 22 M26 6 C26 10 20 16 16 22 M6 20a4 4 0 108 0 M18 20a4 4 0 108 0" },
  ];
  return (
    <svg viewBox="0 0 500 400" xmlns="http://www.w3.org/2000/svg" className={className} role="img" aria-label="Multiple business types — restaurant, retail, convenience, salon — connected to a central payment terminal">
      <defs>
        <linearGradient id={`${id}-panel`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#EAF3FF" /><stop offset="100%" stopColor="#F4F8FF" />
        </linearGradient>
        <linearGradient id={`${id}-accent`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#0072FF" /><stop offset="100%" stopColor="#3A8EFF" />
        </linearGradient>
        <linearGradient id={`${id}-navy`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#0A2552" /><stop offset="100%" stopColor="#061A3A" />
        </linearGradient>
        <radialGradient id={`${id}-halo`} cx="50%" cy="50%" r="45%">
          <stop offset="0%" stopColor="#0072FF" stopOpacity="0.25" />
          <stop offset="70%" stopColor="#0072FF" stopOpacity="0.05" />
          <stop offset="100%" stopColor="#0072FF" stopOpacity="0" />
        </radialGradient>
        <pattern id={`${id}-dots`} x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
          <circle cx="1.4" cy="1.4" r="1.4" fill="#3A8EFF" fillOpacity="0.14" />
        </pattern>
        <filter id={`${id}-shadow`} x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur in="SourceAlpha" stdDeviation="7" /><feOffset dy="12" />
          <feComponentTransfer><feFuncA type="linear" slope="0.24" /></feComponentTransfer>
          <feMerge><feMergeNode /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>

      <rect x="14" y="14" width="472" height="372" rx="28" fill={`url(#${id}-panel)`} />
      <rect x="14" y="14" width="472" height="372" rx="28" fill={`url(#${id}-dots)`} opacity="0.55" />
      <ellipse cx="250" cy="200" rx="200" ry="140" fill={`url(#${id}-halo)`} />

      {/* connection lines from center to each node */}
      {nodes.map((n, i) => (
        <line key={i} x1="250" y1="200" x2={n.x + 32} y2={n.y + 32}
              stroke="#0072FF" strokeOpacity="0.35" strokeWidth="1.6" strokeDasharray="4 4" />
      ))}
      {/* moving dots along paths */}
      {nodes.map((n, i) => (
        <circle key={"d" + i} r="3" fill="#0072FF">
          <animateMotion dur={`${2.4 + i * 0.3}s`} repeatCount="indefinite"
            path={`M250 200 L${n.x + 32} ${n.y + 32}`} />
          <animate attributeName="fill-opacity" values="0;1;0" dur={`${2.4 + i * 0.3}s`} repeatCount="indefinite" />
        </circle>
      ))}

      {/* CENTER — payment terminal */}
      <g transform="translate(198 154)" filter={`url(#${id}-shadow)`}>
        <rect width="104" height="128" rx="16" fill={`url(#${id}-navy)`} />
        <rect x="8" y="8" width="88" height="66" rx="6" fill="#F6FAFF" />
        <rect x="8" y="8" width="88" height="10" rx="6" fill="#EAF3FF" />
        <text x="52" y="46" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="16" fontWeight="900" fill="#061A3A">$—</text>
        <text x="52" y="60" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="7" fontWeight="800" fill="#0072FF">READY</text>
        {/* keypad */}
        <g transform="translate(14 82)">
          {[0,1,2,3,4,5,6,7,8].map(i => (
            <rect key={i} x={(i % 3) * 28} y={Math.floor(i / 3) * 12} width="22" height="8" rx="2.5" fill="#3A8EFF" fillOpacity={i === 4 ? 1 : 0.36} />
          ))}
        </g>
      </g>

      {/* 4 merchant nodes */}
      {nodes.map((n, i) => (
        <g key={"n" + i} transform={`translate(${n.x} ${n.y})`} filter={`url(#${id}-shadow)`}>
          <rect width="64" height="64" rx="14" fill="#FFFFFF" stroke="#061A3A" strokeOpacity="0.08" />
          <rect x="0" y="0" width="64" height="4" rx="2" fill={`url(#${id}-accent)`} />
          <g transform="translate(16 20)" fill="#0072FF" stroke="#0072FF" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
            <path d={n.path} fill={`url(#${id}-accent)`} fillOpacity="0.25" />
          </g>
          <text x="32" y="80" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="9" fontWeight="800" fill="#061A3A">{n.label}</text>
        </g>
      ))}
    </svg>
  );
}

/* ────────────────────────────────────────────────────────────
   Compact Section-6 illustration: service-area radial (abstract
   pins radiating from a hub — NO literal map/skyline)
   ──────────────────────────────────────────────────────────── */
function ServiceAreaIllustration({ className = "" }) {
  const id = "syr6";
  return (
    <svg viewBox="0 0 480 260" xmlns="http://www.w3.org/2000/svg" className={className} role="img" aria-label="Abstract service-area map showing pins radiating from a central hub">
      <defs>
        <linearGradient id={`${id}-accent`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#0072FF" /><stop offset="100%" stopColor="#3A8EFF" />
        </linearGradient>
        <linearGradient id={`${id}-navy`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#0A2552" /><stop offset="100%" stopColor="#061A3A" />
        </linearGradient>
        <radialGradient id={`${id}-halo`} cx="50%" cy="50%" r="55%">
          <stop offset="0%" stopColor="#0072FF" stopOpacity="0.24" />
          <stop offset="70%" stopColor="#0072FF" stopOpacity="0.04" />
          <stop offset="100%" stopColor="#0072FF" stopOpacity="0" />
        </radialGradient>
        <pattern id={`${id}-dots`} x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
          <circle cx="1.4" cy="1.4" r="1.4" fill="#0072FF" fillOpacity="0.18" />
        </pattern>
        <filter id={`${id}-shadow`} x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur in="SourceAlpha" stdDeviation="6" /><feOffset dy="10" />
          <feComponentTransfer><feFuncA type="linear" slope="0.24" /></feComponentTransfer>
          <feMerge><feMergeNode /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>

      <ellipse cx="240" cy="130" rx="230" ry="130" fill={`url(#${id}-halo)`} />
      <rect x="0" y="0" width="480" height="260" fill={`url(#${id}-dots)`} opacity="0.4" />

      {/* orbit rings */}
      {[110, 160, 210].map((r, i) => (
        <ellipse key={i} cx="240" cy="130" rx={r} ry={r * 0.55}
                 fill="none" stroke="#0072FF" strokeOpacity={0.28 - i * 0.07} strokeDasharray="2 4" />
      ))}

      {/* satellite pins */}
      {[
        { x: 80,  y: 92,  s: "sm" },
        { x: 400, y: 76,  s: "sm" },
        { x: 70,  y: 190, s: "sm" },
        { x: 402, y: 200, s: "sm" },
        { x: 140, y: 60,  s: "md" },
        { x: 340, y: 60,  s: "md" },
        { x: 150, y: 208, s: "md" },
        { x: 340, y: 210, s: "md" },
      ].map((p, i) => {
        const r = p.s === "sm" ? 5 : 7;
        return (
          <g key={i} transform={`translate(${p.x} ${p.y})`}>
            <circle r={r + 6} fill="#0072FF" fillOpacity="0.10" />
            <circle r={r} fill={`url(#${id}-accent)`} />
            <circle r={r * 0.4} fill="#FFFFFF" />
          </g>
        );
      })}

      {/* CENTRAL HUB — "Syracuse" pin card */}
      <g transform="translate(184 96)" filter={`url(#${id}-shadow)`}>
        <rect width="112" height="80" rx="14" fill={`url(#${id}-navy)`} />
        <rect width="112" height="26" rx="14" fill={`url(#${id}-accent)`} />
        <rect y="12" width="112" height="14" fill="#0072FF" />
        <g transform="translate(46 4)">
          <path d="M10 0 C6 0 3 3 3 7 c0 5 7 12 7 12 s7 -7 7 -12 c0 -4 -3 -7 -7 -7 z M10 6 a2 2 0 1 0 0.001 0 z" fill="#FFFFFF" />
        </g>
        <text x="56" y="52" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="12" fontWeight="900" fill="#FFFFFF">Syracuse</text>
        <text x="56" y="68" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="8" fontWeight="700" fill="#FFFFFF" fillOpacity="0.65">Central NY hub</text>
      </g>

      {/* Pulses along orbit */}
      <circle r="3" fill="#0072FF">
        <animateMotion dur="7s" repeatCount="indefinite" path="M 240 20 a 160 88 0 1 0 0.001 0" />
      </circle>
      <circle r="2.5" fill="#3A8EFF">
        <animateMotion dur="5s" repeatCount="indefinite" path="M 240 240 a 110 60 0 1 0 -0.001 0" />
      </circle>
    </svg>
  );
}

/* ────────────────────────────────────────────────────────────
   Section-7 illustration: savings comparison — two side-by-side
   plan cards + growing chart, ties to Savings Analysis CTA
   ──────────────────────────────────────────────────────────── */
function SavingsCompareIllustration({ className = "" }) {
  const id = "syr7";
  return (
    <svg viewBox="0 0 460 400" xmlns="http://www.w3.org/2000/svg" className={className} role="img" aria-label="Side-by-side comparison of current processing plan vs an optimized plan, with a savings-trend chart">
      <defs>
        <linearGradient id={`${id}-accent`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#0072FF" /><stop offset="100%" stopColor="#3A8EFF" />
        </linearGradient>
        <linearGradient id={`${id}-navy`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#0A2552" /><stop offset="100%" stopColor="#061A3A" />
        </linearGradient>
        <linearGradient id={`${id}-card`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#FFFFFF" /><stop offset="100%" stopColor="#F6FAFF" />
        </linearGradient>
        <radialGradient id={`${id}-halo`} cx="50%" cy="50%" r="55%">
          <stop offset="0%" stopColor="#0072FF" stopOpacity="0.35" />
          <stop offset="70%" stopColor="#0072FF" stopOpacity="0.06" />
          <stop offset="100%" stopColor="#0072FF" stopOpacity="0" />
        </radialGradient>
        <filter id={`${id}-shadow`} x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur in="SourceAlpha" stdDeviation="9" /><feOffset dy="14" />
          <feComponentTransfer><feFuncA type="linear" slope="0.4" /></feComponentTransfer>
          <feMerge><feMergeNode /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>

      <ellipse cx="230" cy="200" rx="220" ry="170" fill={`url(#${id}-halo)`} />
      <rect x="60" y="52" width="34" height="34" rx="10" fill="#0072FF" fillOpacity="0.18" transform="rotate(-14 77 69)" />
      <rect x="380" y="322" width="40" height="40" rx="12" fill="#3A8EFF" fillOpacity="0.20" transform="rotate(12 400 342)" />
      <circle cx="410" cy="90" r="26" fill="#3A8EFF" fillOpacity="0.20" />
      <circle cx="60" cy="330" r="30" fill="#0072FF" fillOpacity="0.18" />

      {/* LEFT — Current plan card */}
      <g transform="translate(46 88)" filter={`url(#${id}-shadow)`}>
        <rect width="180" height="200" rx="16" fill={`url(#${id}-card)`} stroke="rgba(255,255,255,0.12)" />
        <rect x="0" y="0" width="180" height="6" rx="3" fill="#94A3B8" />
        <text x="14" y="26" fontFamily="Inter, system-ui, sans-serif" fontSize="9" fontWeight="700" fill="#061A3A" fillOpacity="0.55">CURRENT PLAN</text>
        <text x="14" y="54" fontFamily="Inter, system-ui, sans-serif" fontSize="26" fontWeight="900" fill="#061A3A">$1,842</text>
        <text x="14" y="70" fontFamily="Inter, system-ui, sans-serif" fontSize="9" fontWeight="700" fill="#061A3A" fillOpacity="0.5">per month</text>
        <line x1="14" y1="82" x2="166" y2="82" stroke="#061A3A" strokeOpacity="0.1" />
        {[
          { l: "Effective rate", v: "3.42%" },
          { l: "Non-qualified", v: "$248" },
          { l: "Monthly minimum", v: "$25" },
          { l: "PCI + statement", v: "$42" },
        ].map((it, i) => (
          <g key={i} transform={`translate(14 ${94 + i * 22})`} fontFamily="Inter, system-ui, sans-serif">
            <text fontSize="9" fontWeight="700" fill="#061A3A" fillOpacity="0.6">{it.l}</text>
            <text x="152" textAnchor="end" fontSize="9" fontWeight="800" fill="#061A3A">{it.v}</text>
          </g>
        ))}
      </g>

      {/* ARROW between */}
      <g transform="translate(228 168)">
        <path d="M0 20 L28 20 L22 12 M28 20 L22 28" fill="none" stroke="#0072FF" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
      </g>

      {/* RIGHT — Optimized plan card (accent) */}
      <g transform="translate(266 68)" filter={`url(#${id}-shadow)`}>
        <rect width="180" height="220" rx="16" fill={`url(#${id}-navy)`} />
        <rect x="0" y="0" width="180" height="6" rx="3" fill={`url(#${id}-accent)`} />
        <g transform="translate(140 -14)">
          <rect width="52" height="24" rx="12" fill={`url(#${id}-accent)`} />
          <text x="26" y="16" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="9" fontWeight="900" fill="#FFFFFF">SAVE</text>
        </g>
        <text x="14" y="26" fontFamily="Inter, system-ui, sans-serif" fontSize="9" fontWeight="700" fill="#FFFFFF" fillOpacity="0.65">OPTIMIZED PLAN</text>
        <text x="14" y="54" fontFamily="Inter, system-ui, sans-serif" fontSize="26" fontWeight="900" fill="#FFFFFF">$1,430</text>
        <text x="14" y="70" fontFamily="Inter, system-ui, sans-serif" fontSize="9" fontWeight="700" fill="#3A8EFF">per month · –22%</text>
        <line x1="14" y1="82" x2="166" y2="82" stroke="#FFFFFF" strokeOpacity="0.15" />
        {[
          { l: "Effective rate", v: "2.62%" },
          { l: "Non-qualified", v: "$0" },
          { l: "Monthly minimum", v: "waived" },
          { l: "PCI + statement", v: "$18" },
        ].map((it, i) => (
          <g key={i} transform={`translate(14 ${94 + i * 22})`} fontFamily="Inter, system-ui, sans-serif">
            <text fontSize="9" fontWeight="700" fill="#FFFFFF" fillOpacity="0.65">{it.l}</text>
            <text x="152" textAnchor="end" fontSize="9" fontWeight="800" fill="#FFFFFF">{it.v}</text>
          </g>
        ))}
        {/* mini savings ticker */}
        <g transform="translate(14 190)">
          <rect width="152" height="18" rx="9" fill={`url(#${id}-accent)`} />
          <text x="76" y="12" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif" fontSize="9" fontWeight="900" fill="#FFFFFF">≈ $412 / month back</text>
        </g>
      </g>

      {/* Growing chart underneath */}
      <g transform="translate(60 310)">
        <rect width="340" height="72" rx="14" fill="#FFFFFF" fillOpacity="0.08" stroke="#FFFFFF" strokeOpacity="0.14" />
        <text x="16" y="20" fontFamily="Inter, system-ui, sans-serif" fontSize="8" fontWeight="700" fill="#FFFFFF" fillOpacity="0.7">Cumulative savings · 12 months</text>
        <text x="324" y="20" textAnchor="end" fontFamily="Inter, system-ui, sans-serif" fontSize="10" fontWeight="900" fill="#3A8EFF">+$4,944</text>
        {/* line chart */}
        <path d="M20 60 L60 56 L100 50 L140 46 L180 40 L220 34 L260 26 L300 20"
              fill="none" stroke="url(#syr7-accent)" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" />
        {/* dots */}
        {[20, 60, 100, 140, 180, 220, 260, 300].map((x, i) => {
          const y = 60 - i * 5.7;
          return <circle key={i} cx={x} cy={y} r="2.4" fill="#3A8EFF" />;
        })}
      </g>
    </svg>
  );
}

/* ────────────────────────────────────────────────────────────
   UNIFIED HERO — same navy/particles/eyebrow/CTA system used
   across Cash Discount, Free POS, Savings Analysis.
   ──────────────────────────────────────────────────────────── */
function UnifiedHero() {
  return (
    <section className="relative bg-[var(--swipe-navy)] text-white overflow-hidden border-b border-white/10">
      <div className="absolute inset-0 grid-faint opacity-30 pointer-events-none" />
      <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse 55% 65% at 78% 50%, rgba(0,114,255,0.30) 0%, transparent 65%)" }} />
      <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse 40% 50% at 20% 30%, rgba(0,114,255,0.14) 0%, transparent 60%)" }} />
      <div aria-hidden="true" className="absolute -top-24 -left-20 w-[420px] h-[420px] rounded-full pointer-events-none" style={{ background: "rgba(0,114,255,0.18)", filter: "blur(110px)" }} />
      <div aria-hidden="true" className="absolute -bottom-32 right-[12%] w-[480px] h-[480px] rounded-full pointer-events-none" style={{ background: "rgba(0,114,255,0.16)", filter: "blur(120px)" }} />
      <div aria-hidden="true" className="absolute top-[30%] right-[2%] w-2 h-2 rounded-full bg-[var(--swipe-accent)] opacity-70 pointer-events-none" />
      <div aria-hidden="true" className="absolute top-[18%] left-[40%] w-1.5 h-1.5 rounded-full bg-[#7DB6FF] opacity-60 pointer-events-none" />
      <div aria-hidden="true" className="absolute bottom-[20%] left-[8%] w-1.5 h-1.5 rounded-full bg-[#7DB6FF] opacity-50 pointer-events-none" />

      <ParticleField density={36} connectDistance={130} className="absolute inset-0 w-full h-full" />

      <div className="container-swipe relative pt-14 pb-16 lg:pt-20 lg:pb-24">
        <nav className="flex items-center gap-2 text-[13px] text-white/50 mb-8" data-testid="syracuse-breadcrumb">
          <Link to="/" className="hover:text-white">Home</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-white/80">Syracuse, NY</span>
        </nav>

        <div className="grid lg:grid-cols-[1.05fr,1fr] gap-10 lg:gap-12 items-center">
          <Reveal>
            <div className="relative">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/8 border border-white/15 text-[10.5px] uppercase tracking-[0.22em] font-bold text-[#7DB6FF] mb-6">
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--swipe-accent)] animate-pulse" />
                Serving Central New York
              </div>
              <h1 className="font-display-tight text-[2.5rem] sm:text-[3.25rem] lg:text-[4.25rem] xl:text-[4.5rem] font-extrabold leading-[1.05] text-white uppercase tracking-[-0.03em]">
                Merchant Services &amp; <span className="text-[var(--swipe-accent)]">Card Processing</span> in Syracuse, NY
              </h1>
              <p className="mt-7 text-[17px] md:text-[18px] text-white/75 leading-[1.7] max-w-xl">
                Swipetronics helps Syracuse and Central New York businesses accept payments, reduce
                unnecessary processing costs, and choose modern POS and payment solutions with
                dependable local support.
              </p>

              <div className="mt-9 flex flex-wrap gap-3">
                <Link to="/get-started" className="btn-primary-swipe text-[15px] py-3.5 px-7" data-testid="syracuse-hero-get-started">
                  Get Started <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/savings-analysis" className="btn-outline-light text-[15px] py-3.5 px-7" data-testid="syracuse-hero-savings">
                  Request a Savings Analysis
                </Link>
              </div>
            </div>
          </Reveal>

          <Reveal variant="slide-right" delay={120}>
            <div className="relative">
              <LocalPaymentsIllustration className="relative w-full h-auto max-w-[560px] mx-auto" />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* ────────────────────────────────────────────────────────────
   SERVICES SECTION — 6 cards mirroring existing card system
   ──────────────────────────────────────────────────────────── */
const SERVICES = [
  {
    icon: CreditCard,
    title: "Card Processing",
    body: "Secure card processing solutions for businesses accepting credit and debit card payments.",
    to: "/services",
    testId: "syracuse-service-card-processing",
  },
  {
    icon: Monitor,
    title: "POS Systems",
    body: "Modern point-of-sale systems for restaurants, retailers, and other businesses.",
    to: "/pos-systems",
    testId: "syracuse-service-pos-systems",
  },
  {
    icon: TerminalIcon,
    title: "Payment Terminals",
    body: "Standalone card machines and payment terminals for dependable checkout.",
    to: "/products",
    testId: "syracuse-service-terminals",
  },
  {
    icon: Wallet,
    title: "Cash Discount",
    body: "Programs for eligible businesses looking to reduce the cost of card processing.",
    to: "/cash-discount",
    testId: "syracuse-service-cash-discount",
  },
  {
    icon: Gift,
    title: "Free POS Program",
    body: "Eligible businesses may qualify for a modern POS system without a large upfront equipment cost.",
    to: "/free-pos-program",
    testId: "syracuse-service-free-pos",
  },
  {
    icon: Globe,
    title: "Online & E-Commerce Payments",
    body: "Payment gateways, virtual terminals, and online payment-processing solutions.",
    to: "/e-commerce",
    testId: "syracuse-service-ecommerce",
  },
];

const INDUSTRIES = [
  { label: "Restaurants",              to: "/industries/restaurants" },
  { label: "Delis & convenience stores", to: "/industries/convenience-stores-delis" },
  { label: "Grocery stores",           to: "/industries/grocery-stores" },
  { label: "Retail stores",            to: "/industries/retail-stores" },
  { label: "Salons & spas",            to: "/industries/salons-spas" },
  { label: "Service businesses",       to: "/industries/service-businesses" },
  { label: "E-commerce businesses",    to: "/e-commerce" },
  { label: "Other eligible merchants" },
];

const SERVICE_AREAS = [
  "Syracuse", "North Syracuse", "Liverpool", "Cicero", "Clay",
  "DeWitt", "Fayetteville", "Camillus", "Baldwinsville", "East Syracuse",
  "Manlius", "Auburn", "Oswego", "Cortland", "Onondaga County",
];

const WHY = [
  { icon: Compass, t: "Clear card-processing options", d: "Multiple pricing models explained plainly so you can pick what fits your business — not what fits a rep's commission." },
  { icon: TerminalIcon, t: "Modern payment technology", d: "Current-generation terminals and POS systems with tap-to-pay, EMV, tipping, and receipt options." },
  { icon: ShieldCheck, t: "Savings analysis", d: "Send a recent processing statement and we review it line by line to identify avoidable fees." },
  { icon: Wallet, t: "Multiple pricing/program options", d: "Interchange-plus, flat-rate, and cash discount setups — matched to how your business takes payments." },
  { icon: MapPin, t: "Local Central New York support", d: "Serving businesses throughout Syracuse and Central New York with responsive follow-up." },
  { icon: Wrench, t: "Help choosing the right equipment", d: "Guidance on the right terminal, POS, or virtual/e-commerce setup for your business type." },
];

/* ────────────────────────────────────────────────────────────
   PAGE
   ──────────────────────────────────────────────────────────── */
export default function Syracuse() {
  return (
    <div data-testid="syracuse-page">
      <Seo {...getSeoForPath("/merchant-services-syracuse-ny")} />
      <UnifiedHero />

      {/* SECTION 2 — Local positioning */}
      <section className="section bg-white relative overflow-hidden">
        <div aria-hidden="true" className="absolute -top-24 -right-32 w-[420px] h-[420px] rounded-full pointer-events-none" style={{ background: "rgba(0,114,255,0.06)", filter: "blur(90px)" }} />
        <div className="container-swipe relative">
          <div className="grid lg:grid-cols-[1.05fr,1fr] gap-12 lg:gap-16 items-center">
            <div>
              <Reveal>
                <div className="overline mb-4">Central New York</div>
              </Reveal>
              <Reveal delay={80}>
                <h2 className="font-display-tight text-[2rem] sm:text-[2.5rem] lg:text-[3rem] font-extrabold text-[var(--swipe-navy)] leading-[1.12]">
                  Payment solutions for Syracuse businesses.
                </h2>
              </Reveal>
              <Reveal delay={140}>
                <p className="mt-6 text-[18px] text-slate-700 leading-[1.75] max-w-xl font-medium">
                  Your payment setup should make running your business easier — not leave you
                  wondering if you&apos;re paying too much or using the wrong equipment.
                </p>
              </Reveal>
              <Reveal delay={200}>
                <p className="mt-5 text-[16.5px] text-slate-600 leading-[1.8] max-w-xl">
                  Swipetronics helps Syracuse and Central New York businesses find a payment setup
                  that fits the way they actually operate. Whether you need reliable card processing,
                  a modern POS system, payment terminals, or a different pricing program, we&apos;ll
                  help you compare your options and understand what makes sense for your business.
                </p>
              </Reveal>
              <Reveal delay={260}>
                <p className="mt-5 text-[16.5px] text-slate-600 leading-[1.8] max-w-xl">
                  The goal is simple: clearer processing, better payment technology, and dependable
                  support when you need it.
                </p>
              </Reveal>

              {/* Compact trust chips (preserves the previous "local, service-area" info) */}
              <Reveal delay={320}>
                <ul className="mt-8 flex flex-wrap gap-2.5" data-testid="syracuse-trust-chips">
                  {[
                    "Fast, honest side-by-side comparisons",
                    "On-site or remote setup",
                    "Straightforward answers on fees & equipment",
                  ].map((b) => (
                    <li
                      key={b}
                      className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-[var(--swipe-accent-soft)] text-[var(--swipe-navy)] text-[13px] font-semibold border border-[var(--swipe-accent)]/15"
                    >
                      <span className="w-4 h-4 rounded-full bg-[var(--swipe-accent)] text-white flex items-center justify-center shrink-0">
                        <Check className="w-2.5 h-2.5" strokeWidth={3.5} />
                      </span>
                      {b}
                    </li>
                  ))}
                </ul>
              </Reveal>
            </div>

            <Reveal variant="slide-right" delay={140}>
              <div className="relative">
                <StatementReviewIllustration className="relative w-full h-auto max-w-[560px] mx-auto" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* SECTION 3 — Services */}
      <section className="section bg-[var(--swipe-muted)] relative overflow-hidden" data-testid="syracuse-services-section">
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none opacity-40" style={{ backgroundImage: "radial-gradient(circle at 1.5px 1.5px, rgba(0,114,255,0.10) 1.5px, transparent 0)", backgroundSize: "22px 22px" }} />
        <div aria-hidden="true" className="absolute -top-10 -right-10 w-[280px] h-[280px] rounded-full pointer-events-none" style={{ background: "rgba(0,114,255,0.10)", filter: "blur(80px)" }} />
        <div aria-hidden="true" className="absolute -bottom-10 -left-10 w-[260px] h-[260px] rounded-full pointer-events-none" style={{ background: "rgba(0,114,255,0.08)", filter: "blur(80px)" }} />
        <div className="container-swipe relative">
          <div className="max-w-3xl">
            <Reveal><div className="overline mb-4">What We Provide</div></Reveal>
            <Reveal delay={80}>
              <h2 className="font-display-tight text-[2rem] sm:text-[2.5rem] lg:text-[3rem] font-extrabold text-[var(--swipe-navy)] leading-[1.12]">
                Merchant services built around your business.
              </h2>
            </Reveal>
            <Reveal delay={160}>
              <p className="mt-5 text-[17px] text-slate-600 leading-[1.8]">
                The tools most Syracuse businesses need to accept payments cleanly and grow — all
                supported locally by the same team.
              </p>
            </Reveal>
          </div>

          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((s, i) => {
              const Icon = s.icon;
              return (
                <Reveal key={s.title} delay={80 + i * 40}>
                  <Link
                    to={s.to}
                    className="group relative h-full bg-white border border-line rounded-2xl p-7 flex flex-col overflow-hidden transition-all duration-300 ease-out hover:-translate-y-1 hover:border-[var(--swipe-accent)] hover:shadow-[0_18px_44px_-18px_rgba(0,114,255,0.55)]"
                    data-testid={s.testId}
                  >
                    {/* subtle corner decoration */}
                    <div aria-hidden="true" className="absolute -top-8 -right-8 w-24 h-24 rounded-full bg-[var(--swipe-accent)]/8 transition-all duration-500 group-hover:bg-[var(--swipe-accent)]/14 group-hover:scale-110" />
                    <div aria-hidden="true" className="absolute -top-4 -right-4 w-10 h-10 rounded-lg bg-[var(--swipe-accent)]/10 rotate-12 transition-transform duration-500 group-hover:rotate-45" />

                    <div className="relative w-12 h-12 rounded-xl bg-[var(--swipe-accent-soft)] text-[var(--swipe-accent)] flex items-center justify-center group-hover:bg-[var(--swipe-accent)] group-hover:text-white transition-colors duration-300">
                      <Icon className="w-5.5 h-5.5" strokeWidth={2.25} />
                    </div>
                    <h3 className="relative mt-5 font-display text-xl font-black text-[var(--swipe-navy)] leading-tight">{s.title}</h3>
                    <p className="relative mt-3 text-[15px] text-slate-600 leading-relaxed flex-1">{s.body}</p>
                    <span className="relative mt-5 text-sm font-semibold text-[var(--swipe-navy)] inline-flex items-center gap-1.5 group-hover:text-[var(--swipe-accent)]">
                      Learn more <ArrowRight className="w-4 h-4" />
                    </span>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECTION 4 — Industries */}
      <section className="section bg-white" data-testid="syracuse-industries-section">
        <div className="container-swipe">
          <div className="grid lg:grid-cols-[1fr,1fr] gap-12 lg:gap-16 items-center">
            <Reveal>
              <div>
                <div className="overline mb-4">Who We Work With</div>
                <h2 className="font-display-tight text-[2rem] sm:text-[2.5rem] lg:text-[3rem] font-extrabold text-[var(--swipe-navy)] leading-[1.12]">
                  Serving businesses across Syracuse.
                </h2>
                <p className="mt-5 text-[17px] text-slate-600 leading-[1.8] max-w-xl">
                  From countertop payments and mobile invoicing to full POS systems, Swipetronics
                  supports a wide range of business types across Onondaga County and Central New York.
                </p>
                <div className="mt-8">
                  <MerchantMixIllustration className="w-full max-w-[500px]" />
                </div>
                <Link to="/industries" className="btn-outline-swipe mt-8 inline-flex" data-testid="syracuse-industries-cta">
                  See all industries <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </Reveal>
            <Reveal variant="slide-right" delay={120}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {INDUSTRIES.map((ind, i) => {
                  const Wrapper = ind.to ? Link : "div";
                  const wrapperProps = ind.to
                    ? { to: ind.to, "data-testid": `syracuse-industry-link-${ind.to.split("/").pop()}` }
                    : { "data-testid": `syracuse-industry-${i}` };
                  return (
                    <Wrapper
                      key={ind.label}
                      {...wrapperProps}
                      className={`group bg-white border border-line rounded-xl px-4 py-4 flex items-center gap-3 hover:border-[var(--swipe-accent)] hover:shadow-md transition-all ${ind.to ? "cursor-pointer" : ""}`}
                    >
                      <div className="w-8 h-8 rounded-md bg-[var(--swipe-accent-soft)] text-[var(--swipe-accent)] flex items-center justify-center shrink-0 transition-colors group-hover:bg-[var(--swipe-accent)] group-hover:text-white">
                        <Users className="w-4 h-4" strokeWidth={2.25} />
                      </div>
                      <div className="text-[14px] font-semibold text-[var(--swipe-navy)] leading-snug group-hover:text-[var(--swipe-accent)] transition-colors">{ind.label}</div>
                    </Wrapper>
                  );
                })}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* SECTION 5 — Why local */}
      <section className="section bg-[var(--swipe-accent-soft)]/40 border-y border-[var(--swipe-accent)]/15 relative overflow-hidden" data-testid="syracuse-why-section">
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none opacity-40" style={{ backgroundImage: "radial-gradient(circle at 1.5px 1.5px, rgba(0,114,255,0.12) 1.5px, transparent 0)", backgroundSize: "26px 26px" }} />
        <div aria-hidden="true" className="absolute -top-16 right-[10%] w-[300px] h-[300px] rounded-full pointer-events-none" style={{ background: "rgba(0,114,255,0.10)", filter: "blur(90px)" }} />
        <div aria-hidden="true" className="absolute -bottom-20 -left-10 w-[320px] h-[320px] rounded-full pointer-events-none" style={{ background: "rgba(0,114,255,0.08)", filter: "blur(100px)" }} />

        <div className="container-swipe relative">
          <div className="max-w-3xl">
            <Reveal><div className="overline mb-4">Why Swipetronics</div></Reveal>
            <Reveal delay={80}>
              <h2 className="font-display-tight text-[2rem] sm:text-[2.5rem] lg:text-[3rem] font-extrabold text-[var(--swipe-navy)] leading-[1.12]">
                Why work with Swipetronics locally?
              </h2>
            </Reveal>
            <Reveal delay={160}>
              <p className="mt-5 text-[17px] text-slate-600 leading-[1.8]">
                Straightforward guidance, current payment technology, and a real person to call —
                serving businesses throughout Syracuse and Central New York.
              </p>
            </Reveal>
          </div>

          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {WHY.map((b, i) => {
              const Icon = b.icon;
              return (
                <Reveal key={b.t} delay={80 + i * 40}>
                  <div className="relative h-full bg-white border border-[var(--swipe-accent)]/15 rounded-2xl p-6 flex flex-col overflow-hidden">
                    <div aria-hidden="true" className="absolute -top-6 -right-6 w-16 h-16 rounded-full bg-[var(--swipe-accent)]/8" />
                    <div className="relative w-11 h-11 rounded-xl bg-[var(--swipe-accent)] text-white flex items-center justify-center">
                      <Icon className="w-5 h-5" strokeWidth={2.25} />
                    </div>
                    <div className="relative mt-5 font-display text-[16px] font-black text-[var(--swipe-navy)]">{b.t}</div>
                    <p className="relative mt-2 text-[14px] text-slate-600 leading-relaxed">{b.d}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECTION 6 — Local service area */}
      <section className="section bg-white relative" data-testid="syracuse-service-area">
        <div className="container-swipe">
          <div className="rounded-3xl bg-gradient-to-br from-[var(--swipe-accent-soft)] to-[#F4F8FF] border border-[var(--swipe-accent)]/15 p-8 lg:p-14 relative overflow-hidden">
            <div aria-hidden="true" className="absolute -top-20 -right-20 w-[300px] h-[300px] rounded-full pointer-events-none" style={{ background: "rgba(0,114,255,0.12)", filter: "blur(90px)" }} />
            <div className="grid lg:grid-cols-[1.15fr,1fr] gap-10 lg:gap-14 items-center relative">
              <div>
                <Reveal><div className="overline mb-4">Service Area</div></Reveal>
                <Reveal delay={80}>
                  <h2 className="font-display-tight text-[2rem] sm:text-[2.5rem] lg:text-[3rem] font-extrabold text-[var(--swipe-navy)] leading-[1.12]">
                    Serving Syracuse and Central New York.
                  </h2>
                </Reveal>
                <Reveal delay={160}>
                  <p className="mt-5 text-[17px] text-slate-600 leading-[1.8]">
                    Swipetronics supports businesses across the greater Syracuse metro and Central New
                    York region, including:
                  </p>
                </Reveal>

                <Reveal delay={220}>
                  <div className="mt-7 flex flex-wrap gap-2.5">
                    {SERVICE_AREAS.map((area) => (
                      <span
                        key={area}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white text-[var(--swipe-navy)] text-[13.5px] font-semibold border border-[var(--swipe-accent)]/20 shadow-sm"
                        data-testid={`syracuse-area-${area.toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        <MapPin className="w-3.5 h-3.5 text-[var(--swipe-accent)]" strokeWidth={2.4} />
                        {area}
                      </span>
                    ))}
                  </div>
                </Reveal>

                <Reveal delay={280}>
                  <p className="mt-8 text-[14.5px] text-slate-500 leading-relaxed">
                    Don&apos;t see your town listed? We serve most of Onondaga County and the surrounding
                    region — <Link to="/contact" className="text-[var(--swipe-accent)] font-semibold hover:underline">contact us</Link> and
                    we&apos;ll confirm coverage.
                  </p>
                </Reveal>
              </div>

              <Reveal variant="slide-right" delay={140}>
                <div className="relative">
                  <ServiceAreaIllustration className="w-full max-w-[520px] mx-auto" />
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 7 — Savings analysis CTA */}
      <section className="section bg-[var(--swipe-navy)] text-white relative overflow-hidden" data-testid="syracuse-savings-cta">
        <div aria-hidden="true" className="absolute inset-0 grid-faint opacity-30 pointer-events-none" />
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse 60% 60% at 80% 50%, rgba(0,114,255,0.22) 0%, transparent 60%)" }} />
        <div aria-hidden="true" className="absolute -top-24 -left-16 w-[380px] h-[380px] rounded-full pointer-events-none" style={{ background: "rgba(0,114,255,0.16)", filter: "blur(110px)" }} />
        <div className="container-swipe relative">
          <div className="grid lg:grid-cols-[1.05fr,1fr] gap-10 lg:gap-14 items-center">
            <div>
              <div className="overline mb-4" style={{ color: "#7DB6FF" }}>Free Review</div>
              <h2 className="font-display-tight text-[2rem] sm:text-[2.5rem] lg:text-[3rem] font-extrabold leading-[1.12] text-white">
                See what your current processing setup is costing your business.
              </h2>
              <p className="mt-5 text-[17px] text-white/75 leading-[1.75] max-w-xl">
                Send us a recent merchant statement and we&apos;ll walk through your current setup line by
                line — highlighting avoidable fees, comparing available options, and explaining any
                programs your business may be eligible for. No pressure and no obligation.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/savings-analysis" className="btn-primary-swipe text-[15px] py-3.5 px-7" data-testid="syracuse-savings-primary">
                  Request a Savings Analysis <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/services" className="btn-outline-light text-[15px] py-3.5 px-7">
                  Explore services
                </Link>
              </div>
            </div>
            <div className="hidden lg:block">
              <SavingsCompareIllustration className="w-full max-w-[520px] mx-auto" />
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 8 — Final CTA */}
      <section className="section bg-white" data-testid="syracuse-final-cta">
        <div className="container-swipe">
          <div className="rounded-3xl bg-[var(--swipe-navy)] text-white px-8 md:px-14 py-14 lg:py-16 relative overflow-hidden">
            <div aria-hidden="true" className="absolute inset-0 grid-faint opacity-30 pointer-events-none" />
            <div aria-hidden="true" className="absolute -top-20 -right-20 w-[380px] h-[380px] rounded-full pointer-events-none" style={{ background: "rgba(0,114,255,0.20)", filter: "blur(90px)" }} />
            <div className="relative max-w-3xl">
              <h2 className="font-display-tight text-[2rem] sm:text-[2.5rem] lg:text-[3rem] font-extrabold leading-[1.12] text-white">
                Ready to improve the way your business accepts payments?
              </h2>
              <p className="mt-5 text-[17px] text-white/75 leading-[1.75] max-w-2xl">
                Swipetronics helps Syracuse businesses compare card-processing, POS, and equipment
                options — clearly, side by side — so you can pick what actually fits your business.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/get-started" className="btn-primary-swipe text-[15px] py-3.5 px-7" data-testid="syracuse-final-get-started">
                  Get Started <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/contact" className="btn-outline-light text-[15px] py-3.5 px-7" data-testid="syracuse-final-contact">
                  Contact Swipetronics
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
